import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { showErrorToast, showSuccessToast } from '../../../utils/toastUtils';
import { useClient } from '../../client-api';
import { addDocumentToClientPackage } from '../../generated/client';
import { queryKeys } from '../../query-keys';
import { useClientPackages } from '../queries';
import { useCreateClientPackage } from './use-create-client-package';

export const useAddDocument = (): UseMutationResult<void, AxiosError, number> => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: client } = useClient();
    const { draftPackage } = useClientPackages();
    const createClientPackage = useCreateClientPackage();

    return useMutation({
        mutationFn: async (documentId) => {
            let packageId = draftPackage?.id;
            if (draftPackage == null && client != null) {
                const newPackage = await createClientPackage.mutateAsync(client.id);
                packageId = newPackage.id;
            }

            await addDocumentToClientPackage({ path: { id: packageId!, documentId } });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.clientPackagesForClient(client?.id) });
            showSuccessToast(t('clientPackageAddedDocSuccess'));
        },
        onError: (error) => showErrorToast({ error, userMessage: t('failureActionGeneric') }),
    });
};
